// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { CombinedSettings } from 'api/setting'
import {
  ImageryResolution,
  VideoResolution,
  ImageryFormat,
  UploadMethod,
  IntervalType,
  UploadFormat
} from 'constants/setting'
import moment from 'moment'
import Emitter from 'mitt'
import { omit } from 'lodash'

const defaultSetting: ISettingValues = {
  imageryResolution: ImageryResolution._12_MP,
  videoResolution: VideoResolution._720,
  uploadMethod: UploadMethod._4G,
  intervalType: IntervalType.Time,
  captureInterval: 2,
  captureDistanceInterval: 20,
  powerSave: true,
  aiImageQuality: true,
  aiTrafficSign: true,
  aiScene: true,
  aiPoi: true,
  aiPrivacy: true,
  gpsUploadInterval: 300,
  imuUploadInterval: 300,
  workTimeStart: moment('05:30', 'HH:mm'),
  workTimeEnd: moment('18:45', 'HH:mm'),
  trafficQuota: 3,
  alertThreshold: 70, // %
  hardThreshold: 90, // %
  uploadFmt: UploadFormat.Images,
  imageryFmt: ImageryFormat.JPG,
  imageCompressRatio: 95,

  // Internnal settings
  rephotographFlag: true,
  deleteImageFlag: false,
  reCaptureConfigBlurredFlag: false,
  reCaptureConfigCorruptFlag: false,
  reCaptureConfigNormalFlag: false,
  reCaptureConfigOverExposedFlag: false,
  reCaptureConfigUnderExposedFlag: false,
  reCaptureMaxCount: 3
}

export interface ISettingValues extends Omit<CombinedSettings, 'workTimeStart' | 'workTimeEnd'> {
  workTimeStart: moment.Moment
  workTimeEnd: moment.Moment
}

export interface ISettingModelState {
  setting: ISettingValues
  dirty: boolean
}

export class SettingModel {
  private state: ISettingValues
  private originState: ISettingValues

  private dirty: boolean = false
  public readonly emitter = Emitter()

  constructor (setting?: CombinedSettings) {
    if (setting) {
      let state: any = {}
      for (let k in setting) {
        if (k === 'workTimeStart') {
          state.workTimeStart = moment(setting.workTimeStart, 'HH:mm')
        } else if (k === 'workTimeEnd') {
          state.workTimeEnd = moment(setting.workTimeEnd, 'HH:mm')
        } else {
          state[k] = setting[k]
        }
      }
      this.state = state
      this.originState = { ...this.state }
    } else {
      this.state = { ...defaultSetting }
      this.originState = { ...defaultSetting }
    }
  }

  set (key: string, value: unknown) {
    const oldValue = this.state[key]
    this.state[key] = value
    this.checkEquality()
    let hasChanged
    if (key === 'workTimeStart' || key === 'workTimeEnd') {
      hasChanged = !this.isSameTime(oldValue, value as moment.Moment)
    } else {
      hasChanged = oldValue !== value
    }
    if (hasChanged) {
      this.emitter.emit('change', this.getState())
    }
  }

  getState (): ISettingModelState {
    return {
      setting: { ...this.state },
      dirty: this.dirty
    }
  }

  reset () {
    if (this.dirty === false) {
      return
    }
    this.state = { ...this.originState }
    this.dirty = false
    this.emitter.emit('change', this.getState())
  }

  getDirtySettings (): Partial<ISettingValues> {
    let settings: Partial<ISettingValues> = {}
    for (let k in this.state) {
      if (this.isFieldDirty(k)) {
        settings[k] = this.state[k]
      }
    }
    if (settings.intervalType === IntervalType.Time) {
      delete settings.captureDistanceInterval
    } else if (settings.intervalType === IntervalType.Distance) {
      delete settings.captureInterval
    }

    if (settings.uploadFmt === UploadFormat.Images) {
      delete settings.videoResolution
    } else if (settings.uploadFmt === UploadFormat.Video) {
      delete settings.imageryResolution
    }
    return settings
  }

  buildUpdateParam (state: Partial<ISettingValues>): Partial<CombinedSettings> {
    const values: Partial<CombinedSettings> = { ...omit(state, ['workTimeStart', 'workTimeEnd']) }
    if ('workTimeStart' in state) {
      values.workTimeStart = state.workTimeStart.format('HH:mm')
    }
    if ('workTimeEnd' in state) {
      values.workTimeEnd = state.workTimeEnd.format('HH:mm')
    }
    return values
  }

  isDirty (): boolean {
    return this.dirty
  }

  private isFieldDirty (key) {
    let noDirty = true
    if (key === 'workTimeStart' || key === 'workTimeEnd') {
      noDirty = this.isSameTime(this.state[key], this.originState[key])
    } else {
      noDirty = this.state[key] === this.originState[key]
    }
    return !noDirty
  }

  private checkEquality () {
    let noDirty = true
    for (let k in this.state) {
      noDirty = !this.isFieldDirty(k)
      if (noDirty === false) {
        break
      }
    }
    this.dirty = !noDirty
  }

  private isSameTime (time1: moment.Moment, time2: moment.Moment) {
    return time1.isSame(time2, 'hour') && time1.isSame(time2, 'minute')
  }

  dispose () {
    this.emitter.all.clear()
  }
}
